"use client";
import React, { useRef } from "react";
import Image from "next/image";
import { QuizOption } from "../type";
import { SkeletonLoader } from "./SkeletonLoader";
import { OptionsContainer } from "./OptionsContainer";

export interface InlineQuizVariantProps {
  title: string;
  options: QuizOption[];
  selectedOptions: string[];
  optionsVariant?: "basic" | "card";
  selectionType?: "single" | "multiple";
  isLoading?: boolean;
  liveMessage?: string;
  onToggle: (value: string, label: string) => void;
  quizRef?: React.Ref<HTMLDivElement>;
  fieldRef?: React.Ref<HTMLDivElement>;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
}

export const InlineQuizVariant: React.FC<InlineQuizVariantProps> = ({
  title,
  options,
  selectedOptions,
  optionsVariant = "basic",
  selectionType = "single",
  isLoading = false,
  liveMessage,
  onToggle,
  quizRef,
  fieldRef,
  headingRef,
}) => {
  const internalHeadingRef = useRef<HTMLHeadingElement>(null);
  const effectiveHeadingRef = headingRef || internalHeadingRef;

  return (
    <div
      ref={quizRef}
      className="container font-sans rounded-xl flex flex-col md:flex-row md:items-center gap-3 md:gap-4 bg-putty-300 px-4 py-3 md:px-6 md:min-h-[120px]"
    >
      {liveMessage && (
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {liveMessage}
        </div>
      )}
      <div ref={fieldRef} className="flex items-center gap-2 flex-shrink-0">
        <Image
          src="/icons/ai-icon.png"
          alt="ai-icon"
          width={36}
          height={36}
          className="m-0"
        />
        <h3
          ref={effectiveHeadingRef}
          className="text-base md:text-lg font-bold text-gray-900 m-0 max-w-[145px]"
          tabIndex={-1}
        >
          {title}
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {isLoading ? (
          <SkeletonLoader variant="basic" count={5} sectionTitle={title} />
        ) : (
          <OptionsContainer
            options={options}
            selectedOptions={selectedOptions}
            variant={optionsVariant}
            showAllOptions={false}
            onToggle={onToggle}
            selectionType={selectionType}
            sectionTitle={title}
          />
        )}
      </div>
    </div>
  );
};

export default InlineQuizVariant;
